import { type Component, createSignal, Match, onMount, Show, Switch } from "solid-js";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import type { PromoCodesVerifyResponse } from "~/solidJs/entities/PremiumPopUp/helpers/IProducts";
import { PremiumPopUpContext } from "~/solidJs/entities/PremiumPopUp/models/premiumPopUpContext";
import type { promocodeKind } from "~/solidJs/entities/PremiumPopUp/types";
import { PricingPageMethods } from "~/solidJs/pages/PricingPage";
import { ProductsApi } from "~/solidJs/shared/api/ProductsApi";
import { getPrivateMessageFromError, useCustomContext } from "~/solidJs/shared/helpers";
import { Button, ErrorMessage, Input } from "~/solidJs/shared/ui";

type PromoCodeFeatureProps = Record<string, never>;
export type SuccessProps = {
	promoCode: string;
	kind: promocodeKind;
	amount: number;
};
export const usePromoCodeFeature = (props: { onSuccess?: (promoCode: SuccessProps) => void }) => {
	const [getPromoErrorState, setPromoErrorState] = createSignal<null | string>(null);
	const [getIsPending, setIsPending] = createSignal(false);
	const [getPromoCodeState, setPromoCodeState] = createSignal<SuccessProps | null>(null);
	const applyPromocodeHandler = async (state: string) => {
		EventAnalytics.promocodeApplied(state);
		if (!state.length) {
			setPromoErrorState("Enter a valid Promo Code");
			return;
		}
		setPromoCodeState(null);
		setIsPending(true);
		let resp: PromoCodesVerifyResponse;
		try {
			resp = await ProductsApi.getPormocodePrices(state);
		} catch (e) {
			EventAnalytics.sendErrorInfo("get-promocode-error", {
				message: getPrivateMessageFromError(e),
				state
			});
			setPromoErrorState("The unknown error happened");
			setIsPending(false);
			return;
		}
		const kind = resp.kind;
		const amount = resp.percent_off;
		try {
			if (kind === null || amount === null) {
				setPromoErrorState("Invalide code. Please try again");
				setIsPending(false);
				return;
			}
		} catch (e) {
			EventAnalytics.sendErrorInfo("set-invaild-promocode-state-error", {
				message: getPrivateMessageFromError(e),
				state
			});
			setPromoErrorState("The unknown error happened");
			setIsPending(false);
			return;
		}
		try {
			setPromoErrorState(null);
		} catch (e) {
			EventAnalytics.sendErrorInfo("reset-promocode-error-state", {
				message: getPrivateMessageFromError(e),
				state
			});
			setPromoErrorState("The unknown error happened");
		}
		try {
			setPromoCodeState({ promoCode: state, kind, amount });
			props.onSuccess?.({
				promoCode: state,
				kind,
				amount
			});
		} catch (e) {
			EventAnalytics.sendErrorInfo("apply-promocode-handler-fc-error", {
				message: getPrivateMessageFromError(e),
				state
			});
			setPromoErrorState("The unknown error happened");
		}
		setIsPending(false);
	};
	return {
		getPromoErrorState,
		setPromoErrorState,
		getIsPending,
		applyPromocodeHandler,
		getPromoCode: getPromoCodeState
	};
};

export const PromoCodeFeatureV2: Component<PromoCodeFeatureProps> = (_props) => {
	const [getIsVisible, setIsVisible] = createSignal(false);
	const [getPromoCodeState, setPromoCodeState] = createSignal("");
	const state = useCustomContext(PricingPageMethods.PricingPageContext);

	const onApply = async () => {
		state.setPromo(getPromoCodeState());
	};

	onMount(() => {
		setPromoCodeState(state.getPromo());
	});

	return (
		<Switch>
			<Match when={getIsVisible() || state.getPromo()}>
				<form
					onSubmit={(e) => {
						e.preventDefault();
					}}
					class="relative flex w-full items-center justify-center gap-0.5"
				>
					<Input
						value={getPromoCodeState()}
						placeholder="Promocode"
						class="w-[140px] rounded border-none bg-interaction-grey-light p-2 font-medium text-xs leading-4"
						onInput={(e) => setPromoCodeState(e.currentTarget.value.toUpperCase())}
					/>
					<Show when={state.usePromocode.getPromoErrorState()} keyed>
						{(err) => (
							<ErrorMessage class="-translate-x-1/2 absolute top-full left-1/2 mt-1 whitespace-nowrap p-2 text-center">
								{err}
							</ErrorMessage>
						)}
					</Show>
					<Button
						pending={state.usePromocode.getIsPending()}
						variant={"TertiaryAccent"}
						type="submit"
						disabled={!getPromoCodeState().length}
						onClick={onApply}
					>
						{state.usePromocode.getPromoCode() ? "Applied!" : "Apply"}
					</Button>
					<Show when={state.usePromocode.getPromoCode()} keyed>
						{(promoInfo) => (
							<div class="flex h-5 shrink-0 items-center rounded-full bg-interaction-violet-light p-1 text-[10px] text-interaction-violet">
								–{promoInfo.amount}%
							</div>
						)}
					</Show>
				</form>
			</Match>
			<Match when={!getIsVisible()}>
				<Button
					variant="FlexButtonSecondary"
					onClick={() => {
						EventAnalytics.pricing.addPromoCode();
						setIsVisible(true);
					}}
				>
					Add promocode
				</Button>
			</Match>
		</Switch>
	);
};
export const PromoCodeFeature: Component<PromoCodeFeatureProps> = (_props) => {
	const [getPromoCodeState, setPromoCodeState] = createSignal("");
	const { setPremiumPopUpStore, getPremiumPopUpStore } = useCustomContext(PremiumPopUpContext);
	const onSuccess = (props: SuccessProps) => {
		setPremiumPopUpStore((prev) => ({
			...prev,
			promocodeChange: props
		}));
	};
	const { getPromoErrorState, getIsPending, applyPromocodeHandler } = usePromoCodeFeature({
		onSuccess
	});

	const onApply = async () => {
		applyPromocodeHandler(getPromoCodeState());
	};
	return (
		<form
			onSubmit={(e) => {
				e.preventDefault();
			}}
			class="relative flex items-center"
		>
			<Input
				value={getPromoCodeState()}
				placeholder="Promocode"
				onInput={(e) => setPromoCodeState(e.currentTarget.value.toUpperCase())}
			/>
			<Show when={getPromoErrorState()} keyed>
				{(err) => (
					<ErrorMessage class="-translate-x-1/2 absolute top-full left-1/2 mt-1 whitespace-nowrap p-2 text-center">
						{err}
					</ErrorMessage>
				)}
			</Show>
			<Button
				pending={getIsPending()}
				type="submit"
				disabled={!getPromoCodeState().length}
				onClick={onApply}
			>
				Apply
			</Button>
			<Show when={getPremiumPopUpStore().promocodeChange} keyed>
				{(promoInfo) => (
					<div class="flex h-5 shrink-0 items-center rounded-full bg-interaction-violet-light p-1 text-[10px] text-interaction-violet">
						–{promoInfo.amount}%
					</div>
				)}
			</Show>
		</form>
	);
};
